import React, { useMemo } from 'react';
import { Box, Checkbox } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { Typography } from 'src/components/Typography';
import { WorkerActionsButton } from 'src/components/WorkerActionsButton';
import { WorkerNameCell } from 'src/pages/Workers/components/WorkerNameCell';
import { WorkerStatusCell } from 'src/pages/Workers/components/WorkerStatusCell';
import { BUTTON_COLOR, BUTTON_VARIANT } from 'src/components/Button/types';
import { WorkerProfile } from 'src/DTO/WorkerProfile.type';
import { WorkerColumnCheckboxProps } from 'src/hooks/useWorkerColumns.types';
import { getWorkerNameWithAge } from 'src/utils/getWorkerNameWithAge';
import { getJobsTitle, handleOpenWorkerProfile } from 'src/utils/workerUtils';

const columnHelper = createColumnHelper<WorkerProfile>();

const useWorkerColumnDefinitions = ({
  handleToggle,
  selectedWorkers,
  openActions,
}: WorkerColumnCheckboxProps & { openActions?: (worker: WorkerProfile) => void } = {}) => {
  const { t } = useTranslation();

  const columns = useMemo(() => {
    const nameColumn = columnHelper.accessor('name', {
      header: () => t('workersScreen.worker'),
      cell: ({ row: { original: worker } }) => {
        const isSelected = selectedWorkers?.some(w => w.id === worker.id);
        const lastImageUrl = worker.imageUrls?.[worker.imageUrls.length - 1];

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'left' }}>
            {selectedWorkers && handleToggle && (
              <Checkbox
                checked={isSelected}
                onChange={() => handleToggle(worker)}
                sx={{
                  color: 'primary.main',
                  '&.Mui-checked': { color: 'primary.main' },
                  marginRight: '15px',
                }}
              />
            )}
            <WorkerNameCell
              name={getWorkerNameWithAge(worker)}
              handleOpenWorkerProfile={() => handleOpenWorkerProfile(worker)}
              image={lastImageUrl}
            />
          </Box>
        );
      },
    });

    const phoneNumberColumn = columnHelper.accessor('phoneNumber', {
      header: () => t('workersScreen.phone'),
      cell: ({ getValue }) => getValue(),
    });

    const statusColumn = columnHelper.accessor('poolStatus', {
      header: () => t('workersScreen.status'),
      cell: ({ getValue }) => <WorkerStatusCell poolStatus={getValue()} />,
    });

    const jobColumn = columnHelper.accessor(worker => getJobsTitle(worker, t), {
      id: 'jobsTitle',
      header: () => t('workersScreen.job'),
      cell: ({ getValue }) => getValue(),
      enableColumnFilter: false,
    });

    const actionsColumn = columnHelper.display({
      id: 'actions',
      header: () => t('workersScreen.actions'),
      cell: ({ row: { original: worker } }) => (
        <WorkerActionsButton
          label={t('workersScreen.actions')}
          color={BUTTON_COLOR.INFO}
          variant={BUTTON_VARIANT.GRADIENT}
          onClick={() => !!openActions && openActions(worker)}
        />
      ),
    });

    const interviewColumn = columnHelper.accessor('interviewId', {
      header: () => t('workersScreen.interview.label'),
      cell: ({ row: { original: worker } }) => {
        const { interviewId, id } = worker;

        return (
          <WorkerActionsButton
            color={interviewId ? BUTTON_COLOR.SUCCESS : BUTTON_COLOR.ERROR}
            label={interviewId ? t('generic.open') : t('workersScreen.noLink')}
            variant={BUTTON_VARIANT.GRADIENT}
            onClick={() => interviewId && window.open(`/worker/${id}`, '_blank')}
            disabled={!interviewId}
          />
        );
      },
    });

    const preferredLocationsColumn = columnHelper.accessor('preferredLocations', {
      header: () => t('workersScreen.preferredLocations'),
      cell: ({ getValue }) => {
        const locations = getValue();

        return (
          <Typography variant="caption">
            {locations?.length ? locations.join(', ') : '-'}
          </Typography>
        );
      },
    });

    return {
      nameColumn,
      phoneNumberColumn,
      statusColumn,
      jobColumn,
      actionsColumn,
      interviewColumn,
      preferredLocationsColumn,
    };
  }, [t, handleToggle, selectedWorkers]);

  return columns;
};

/**
 * To have selectable workers, provide `selectedWorkers` and `handleToggle` props.
 * To have actions column, provide `openActions` prop.
 */
export const getWorkerColumns = ({
  selectedWorkers,
  handleToggle,
  openActions,
}: WorkerColumnCheckboxProps & { openActions?: (worker: WorkerProfile) => void } = {}) => {
  const { nameColumn, phoneNumberColumn, statusColumn, jobColumn, actionsColumn } =
    useWorkerColumnDefinitions({ handleToggle, selectedWorkers, openActions });

  const defaultCols = [nameColumn, phoneNumberColumn, statusColumn, jobColumn];

  return !!openActions ? [...defaultCols, actionsColumn] : defaultCols;
};

/**
 * To have selectable workers, provide `selectedWorkers` and `handleToggle` props.
 */
export const getWorkerColumnsActive = ({
  handleToggle,
  selectedWorkers,
}: WorkerColumnCheckboxProps = {}) => {
  const { nameColumn, interviewColumn, preferredLocationsColumn } = useWorkerColumnDefinitions({
    handleToggle,
    selectedWorkers,
  });

  return [nameColumn, preferredLocationsColumn, interviewColumn];
};
